import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useToast, ConfirmDialog, InlineSpinner } from './UI';

// Settings > Data Privacy & Security. One switch, Sensitive Data Protection, for every AI feature
// (server/services/ai.service.js): on = student names and IDs become labels, and emails, phone
// numbers, ID and card numbers are hidden, before anything is sent to OpenAI. OpenAI is always
// told not to keep a copy. Shown to admin and super_admin only (SettingsPage); the API enforces
// the same.
export default function AiPrivacyCard() {
  const toast = useToast();
  const [enabled,    setEnabled]    = useState(null); // null until loaded
  const [error,      setError]      = useState('');
  const [saving,     setSaving]     = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/settings/ai-privacy')
      .then(r => { if (active) setEnabled(!!r.data.data.enabled); })
      .catch(e => { if (active) setError(e.response?.data?.message || 'Could not load this setting'); });
    return () => { active = false; };
  }, []);

  const save = async next => {
    setSaving(true);
    try {
      const r = await api.put('/settings/ai-privacy', { enabled: next });
      setEnabled(!!r.data.data.enabled);
      toast(next ? 'Sensitive Data Protection is on' : 'Sensitive Data Protection is off', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Could not save the setting', 'error');
    } finally {
      setSaving(false);
      setConfirmOff(false);
    }
  };

  const deleteStored = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      const { found, failed } = (await api.delete('/settings/ai-privacy/stored-data')).data.data;
      if (failed) toast(`Deleted ${found - failed} of ${found} stored chats. ${failed} could not be deleted, try again.`, 'error');
      else toast(found ? `Deleted ${found} stored AI chat${found === 1 ? '' : 's'} at OpenAI` : 'OpenAI had no stored chats to delete', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Could not delete the stored chats', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Turning protection off exposes more, so it needs a confirm; turning it on does not.
  const onToggle = () => (enabled ? setConfirmOff(true) : save(true));

  const line = (ok, text) => (
    <div className="d-flex align-items-start gap-2" style={{ fontSize: 14, color: 'var(--sms-txt)' }}>
      <i className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`} aria-hidden="true"
        style={{ marginTop: 3, color: ok ? 'var(--sms-ok-strong)' : 'var(--sms-bad-strong)' }} />
      <span>{text}</span>
    </div>
  );

  return (
    <div className="sms-card">
      <div className="d-flex align-items-center gap-3 mb-3">
        <div aria-hidden="true" style={{
          width: 40, height: 40, borderRadius: 10, flex: '0 0 auto', fontSize: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--sms-acc-bg)', color: 'var(--sms-acc)',
        }}>
          <i className="bi bi-shield-lock-fill" />
        </div>
        <div>
          <h6 style={{ fontSize: 14, fontWeight: 600, color: 'var(--sms-txt)', marginBottom: 2 }}>Data Privacy &amp; Security</h6>
          <div style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>Keeps student details safe when AI is used.</div>
        </div>
      </div>

      {error && <div role="alert" style={{ fontSize: 14, color: 'var(--sms-btn-outline-danger-txt)' }}>{error}</div>}

      {enabled === null && !error && (
        <div className="d-flex align-items-center gap-2" role="status" style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>
          <InlineSpinner size={14} /> Loading…
        </div>
      )}

      {enabled !== null && (
        <>
          <div className="d-flex align-items-start gap-3 mb-3" style={{
            padding: '14px 16px', borderRadius: 12,
            background: enabled ? 'var(--sms-acc-bg)' : 'var(--sms-sur2)',
            border: `1px solid ${enabled ? 'var(--sms-acc)' : 'var(--sms-bdr)'}`,
          }}>
            <button type="button" role="switch" aria-checked={enabled} aria-label="Sensitive Data Protection"
              className="sms-switch" style={{ marginTop: 2 }} disabled={saving} onClick={onToggle} />
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sms-txt)' }}>Sensitive Data Protection</span>
                <span style={{
                  fontSize: 12, fontWeight: 700, letterSpacing: '.04em', padding: '2px 9px', borderRadius: 999,
                  color: enabled ? 'var(--sms-ok-on)' : 'var(--sms-bad-on)',
                  background: enabled ? 'var(--sms-ok-strong)' : 'var(--sms-bad-strong)',
                }}>{enabled ? 'ON' : 'OFF'}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--sms-txt)', marginTop: 3 }}>Covers every AI feature, including AI Insights.</div>
            </div>
          </div>

          <div className="d-flex flex-column gap-2 mb-3">
            {line(true, 'OpenAI does not keep a copy of what it is sent.')}
            {enabled ? (
              <>
                {line(true, 'Student names and IDs are swapped for labels (Student 1, Student 2…) before sending, and put back in the answer here.')}
                {line(true, 'Emails, phone numbers, ID numbers and card numbers are not stored by the AI.')}
              </>
            ) : line(false, 'Student names, emails, phone numbers, ID numbers and card numbers are sent to the AI as they are.')}
          </div>

          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap"
            style={{ borderTop: '1px solid var(--sms-bdr)', marginTop: 16, paddingTop: 14, fontSize: 14, color: 'var(--sms-txt)' }}>
            <span>AI results are made on demand and are not saved.</span>
            <button type="button" className="sms-btn sms-btn-outline-danger" style={{ fontSize: 13, padding: '7px 14px' }} disabled={deleting}
              onClick={() => setConfirmDelete(true)}>
              {deleting ? <InlineSpinner size={12} /> : <i className="bi bi-trash me-1" aria-hidden="true" />}
              Delete stored AI chats
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        show={confirmOff}
        danger
        confirmLabel="Turn off"
        message="Turn off Sensitive Data Protection? Student names, emails, phone numbers, ID numbers and card numbers will be sent to OpenAI as they are."
        onCancel={() => setConfirmOff(false)}
        onConfirm={() => save(false)}
      />

      <ConfirmDialog
        show={confirmDelete}
        danger
        confirmLabel="Delete"
        message="Delete every AI chat OpenAI is still holding for this portal? This cannot be undone. OpenAI's own safety record (up to 30 days) is not affected."
        onCancel={() => setConfirmDelete(false)}
        onConfirm={deleteStored}
      />
    </div>
  );
}
